import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

const authHeader = (authorization: string) => ({
    "Content-Type": "application/json",
    Authorization: authorization,
})

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data && (data as { codErro?: number }).codErro !== undefined && data.codErro! > 0) {
        return { retornoErro: data.codErro!, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

// ─── Modelos ─────────────────────────────────────────────────────────────────

/**
 * Um alerta exibido no sino de avisos do painel. Vive no cache (Redis), não em
 * tabela: a chave carrega a ordem de exibição e o próprio TTL é a validade.
 */
export interface AlertaPainel {
    chave: string
    conteudo: string
    ordem: number | null
    segundosRestantes: number
}

export interface NovoAlertaPainel {
    /** Preenchida na edição: o backend descarta a chave antiga e gera outra. */
    chave?: string
    ordem: number
    /** Validade em segundos (vira o TTL da chave no cache). */
    validade: number
    conteudo: string
}

/** As mesmas opções de validade do select da tela do sistema administrativo. */
export const VALIDADES_ALERTA: { valor: number; rotulo: string }[] = [
    { valor: 60, rotulo: "1 minuto" },
    { valor: 300, rotulo: "5 minutos" },
    { valor: 900, rotulo: "15 minutos" },
    { valor: 3600, rotulo: "Uma hora" },
    { valor: 86400, rotulo: "Um dia" },
    { valor: 604800, rotulo: "Uma semana" },
    { valor: 2592000, rotulo: "Um mês" },
    { valor: 31536000, rotulo: "Um ano" },
    { valor: 315360000, rotulo: "Dez anos" },
]

/** Converte o TTL restante em algo legível na listagem. */
export function formatarTempoRestante(segundos: number): string {
    if (!segundos || segundos <= 0) return "Sem expiração"
    const dias = Math.floor(segundos / 86400)
    if (dias >= 1) return dias === 1 ? "1 dia" : `${dias} dias`
    const horas = Math.floor(segundos / 3600)
    if (horas >= 1) return horas === 1 ? "1 hora" : `${horas} horas`
    const minutos = Math.max(1, Math.floor(segundos / 60))
    return minutos === 1 ? "1 minuto" : `${minutos} minutos`
}

// ─── Chamadas ────────────────────────────────────────────────────────────────

export const listarAlertasPainel = async function (
    authorization: string
): Promise<AlertaPainel[] | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<AlertaPainel[]>>("fornecedor/ListarAlertasPainel", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<AlertaPainel[]>(response, "listarAlertasPainel")
    } catch (error) {
        RegistrarLogErro(error, "listarAlertasPainel", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarAlertaPainel = async function (
    authorization: string,
    alerta: NovoAlertaPainel
): Promise<{ chave: string } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ chave: string }>>("fornecedor/SalvarAlertaPainel", alerta, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<{ chave: string }>(response, "salvarAlertaPainel")
    } catch (error) {
        RegistrarLogErro(error, "salvarAlertaPainel", JSON.stringify(alerta))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

/**
 * Gera o HTML do alerta a partir de uma descrição em texto. A chamada à Anthropic
 * acontece no backend — a chave da API nunca chega ao navegador.
 */
export const gerarAlertaPainelIA = async function (
    authorization: string,
    descricao: string
): Promise<{ html: string } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ html: string }>>(
                "fornecedor/GerarAlertaPainelIA",
                { descricao },
                { headers: authHeader(authorization) }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<{ html: string }>(response, "gerarAlertaPainelIA")
    } catch (error) {
        RegistrarLogErro(error, "gerarAlertaPainelIA", descricao)
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const excluirAlertaPainel = async function (
    authorization: string,
    chave: string
): Promise<{ result: string } | ErroApi> {
    try {
        const response = await api
            .delete<RespostaRaw<{ result: string }>>("fornecedor/ExcluirAlertaPainel", {
                headers: authHeader(authorization),
                params: { chave },
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<{ result: string }>(response, "excluirAlertaPainel")
    } catch (error) {
        RegistrarLogErro(error, "excluirAlertaPainel", chave)
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
